import { useState, useEffect, useCallback } from "react";
import { useAuth, useTheme } from "@/App";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DndContext,
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  Plus,
  Sun,
  LogOut,
  Calendar,
  CalendarDays,
  Inbox,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  Trash2,
  Edit3,
  Menu,
  X,
  Settings,
  Search,
  Bell,
  LayoutGrid,
  List,
  Filter,
  Zap,
  CheckCircle2,
  Clock,
  Users,
  Lock,
  FileText,
  Link2,
  MessageCircle,
  Copy,
} from "lucide-react";
import TaskModal from "@/components/TaskModal";
import ListModal from "@/components/ListModal";
import BoardColumn from "@/components/BoardColumn";
import TaskCard from "@/components/TaskCard";
import TaskDetailPanel from "@/components/TaskDetailPanel";
import GroupMembersModal from "@/components/TeamPanel";
import ChatPanel from "@/components/ChatPanel";
import MemberTasksPanel from "@/components/MemberTasksPanel";
import AdminUpdatesPanel from "@/components/AdminUpdatesPanel";
import MyDayView from "@/components/MyDayView";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const [lists, setLists] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [defaultBoardEmojis, setDefaultBoardEmojis] = useState({
    "urgent-important": "🔴",
    "followups": "🔵",
    "important-regular": "🟣",
    "event-followups": "🟢",
  });
  const defaultBoards = [
    { id: "urgent-important", name: "Urgent & Important", color: "#e74c3c", isDefault: true, emoji: defaultBoardEmojis["urgent-important"] },
    { id: "followups", name: "Followups with team", color: "#3498db", isDefault: true, emoji: defaultBoardEmojis["followups"] },
    { id: "important-regular", name: "Important & Regular", color: "#9b59b6", isDefault: true, emoji: defaultBoardEmojis["important-regular"] },
    { id: "event-followups", name: "Event Followups", color: "#27ae60", isDefault: true, emoji: defaultBoardEmojis["event-followups"] },
  ];
  const [hiddenBoards, setHiddenBoards] = useState([]);
  const [selectedView, setSelectedView] = useState("my-day");
  const [activeGroupId, setActiveGroupId] = useState(null); // Currently viewed group
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);
  const [customViewsOpen, setCustomViewsOpen] = useState(false);
  const [myListsOpen, setMyListsOpen] = useState(true);
  
  // Drag state
  const [activeTask, setActiveTask] = useState(null);
  
  // Modals
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [listModalOpen, setListModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingList, setEditingList] = useState(null);
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [detailTask, setDetailTask] = useState(null);
  
  // Custom tags cache for task cards
  const [customTags, setCustomTags] = useState([]);
  
  // Groups state
  const [groups, setGroups] = useState([]);
  const [groupMembers, setGroupMembers] = useState({});
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(true);
  const [groupActionModal, setGroupActionModal] = useState(false); // "create" | "join" | false
  const [newGroupName, setNewGroupName] = useState("");
  const [joinLinkInput, setJoinLinkInput] = useState("");
  const [joiningGroup, setJoiningGroup] = useState(false);
  
  // Custom sections
  const [customSections, setCustomSections] = useState([]);
  
  // Combine default + custom boards, filter out hidden
  const allBoards = [
    ...defaultBoards.filter(b => !hiddenBoards.includes(b.id)),
    ...customSections.map(s => ({ id: s.section_id, name: s.name, color: s.color, emoji: s.emoji || "", isDefault: false })),
  ];
  
  // Member filter
  const [filterMemberId, setFilterMemberId] = useState(null);
  
  // Chat state
  const [chatGroup, setChatGroup] = useState(null);
  const [chatIsAdmin, setChatIsAdmin] = useState(false);
  
  // Admin member view state
  const [viewMember, setViewMember] = useState(null);
  const [viewMemberGroup, setViewMemberGroup] = useState(null);
  
  // Member notification counts: { group_id: { member_id: count } }
  const [memberNotifications, setMemberNotifications] = useState({});
  
  // Admin-to-member notification counts: { group_id: count } (for non-admin members)
  const [adminNotifications, setAdminNotifications] = useState({});
  
  // Member viewing admin updates panel state
  const [viewAdmin, setViewAdmin] = useState(null);
  const [viewAdminGroup, setViewAdminGroup] = useState(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const fetchData = useCallback(async () => {
    try {
      const [listsRes, tasksRes, groupsRes, tagsRes, sectionsRes] = await Promise.all([
        fetch(`${API}/lists`, { credentials: "include" }),
        fetch(`${API}/tasks`, { credentials: "include" }),
        fetch(`${API}/groups`, { credentials: "include" }),
        fetch(`${API}/tags`, { credentials: "include" }),
        fetch(`${API}/sections`, { credentials: "include" }),
      ]);

      if (listsRes.ok) setLists(await listsRes.json());
      if (tasksRes.ok) setTasks(await tasksRes.json());
      if (tagsRes.ok) setCustomTags(await tagsRes.json());
      if (sectionsRes.ok) setCustomSections(await sectionsRes.json());
      if (groupsRes.ok) {
        const groupsData = await groupsRes.json();
        setGroups(groupsData);
        // Fetch members and notifications for each group
        for (const g of groupsData) {
          const mRes = await fetch(`${API}/groups/${g.group_id}/members`, { credentials: "include" });
          if (mRes.ok) {
            const mData = await mRes.json();
            setGroupMembers(prev => ({ ...prev, [g.group_id]: mData }));
          }
          // Fetch notification counts (for admins)
          if (g.is_owner) {
            const nRes = await fetch(`${API}/groups/${g.group_id}/member-notifications`, { credentials: "include" });
            if (nRes.ok) {
              const nData = await nRes.json();
              setMemberNotifications(prev => ({ ...prev, [g.group_id]: nData }));
            }
          } else {
            // Fetch admin-to-member notifications (for non-admin members)
            const anRes = await fetch(`${API}/groups/${g.group_id}/admin-notifications`, { credentials: "include" });
            if (anRes.ok) {
              const anData = await anRes.json();
              setAdminNotifications(prev => ({ ...prev, [g.group_id]: anData.count || 0 }));
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh tasks every 10 seconds for real-time comment updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handle deep link from email reply (e.g., ?openTask=xxx&group=yyy)
  useEffect(() => {
    // Save deep link params to localStorage on first load (before auth redirect might clear them)
    const params = new URLSearchParams(window.location.search);
    const openTaskId = params.get("openTask");
    const groupId = params.get("group");
    if (openTaskId) {
      localStorage.setItem("deepLink_openTask", openTaskId);
      if (groupId) localStorage.setItem("deepLink_group", groupId);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // Process deep link after data is loaded
  useEffect(() => {
    if (loading || !user) return;
    const openTaskId = localStorage.getItem("deepLink_openTask");
    const groupId = localStorage.getItem("deepLink_group");
    if (!openTaskId) return;

    // Clear stored deep link
    localStorage.removeItem("deepLink_openTask");
    localStorage.removeItem("deepLink_group");

    // Switch to group context
    if (groupId) {
      setActiveGroupId(groupId);
      setSelectedView("all");
    }

    // Fetch the specific task from API (works for both own tasks and group member tasks)
    const fetchAndOpenTask = async () => {
      try {
        const res = await fetch(`${API}/tasks/${openTaskId}`, { credentials: "include" });
        if (res.ok) {
          const task = await res.json();
          setDetailTask(task);
        }
      } catch (err) {
        console.error("Failed to open task from deep link:", err);
      }
    };
    fetchAndOpenTask();
  }, [loading, user]);

  // Poll notification counts every 30s
  useEffect(() => {
    const pollNotifications = async () => {
      for (const g of groups) {
        try {
          if (g.is_owner) {
            const nRes = await fetch(`${API}/groups/${g.group_id}/member-notifications`, { credentials: "include" });
            if (nRes.ok) {
              const nData = await nRes.json();
              setMemberNotifications(prev => ({ ...prev, [g.group_id]: nData }));
            }
          } else {
            const anRes = await fetch(`${API}/groups/${g.group_id}/admin-notifications`, { credentials: "include" });
            if (anRes.ok) {
              const anData = await anRes.json();
              setAdminNotifications(prev => ({ ...prev, [g.group_id]: anData.count || 0 }));
            }
          }
        } catch { /* silent */ }
      }
    };
    if (groups.length > 0) {
      const interval = setInterval(pollNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [groups]);

  const handleToggleTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}/toggle`, {
        method: "PATCH",
        credentials: "include",
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks((prev) =>
          prev.map((t) => (t.task_id === taskId ? updatedTask : t))
        );
        if (updatedTask.completed) {
          toast.success("Task completed!");
        }
      }
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      const response = await fetch(`${API}/tasks/${taskId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setTasks((prev) => prev.filter((t) => t.task_id !== taskId));
        toast.success("Task deleted");
      }
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const handleDeleteList = async (listId) => {
    try {
      const response = await fetch(`${API}/lists/${listId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        setLists((prev) => prev.filter((l) => l.list_id !== listId));
        setTasks((prev) => prev.filter((t) => t.list_id !== listId));
        toast.success("List deleted");
      }
    } catch (error) {
      toast.error("Failed to delete list");
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = "/";
  };

  const handleCreateGroup = async (name) => {
    const groupName = name || newGroupName;
    if (!groupName.trim()) return;
    try {
      const res = await fetch(`${API}/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: groupName.trim() }),
      });
      if (res.ok) {
        const group = await res.json();
        setGroups((prev) => [...prev, group]);
        setGroupMembers((prev) => ({ ...prev, [group.group_id]: [] }));
        setNewGroupName("");
        setGroupActionModal(false);
        // Open the members modal for the new group
        setSelectedGroup(group);
        setGroupModalOpen(true);
        toast.success("Group created!");
      }
    } catch {
      toast.error("Failed to create group");
    }
  };

  const handleJoinGroup = async () => {
    if (!joinLinkInput.trim()) {
      toast.error("Please paste an invite link");
      return;
    }
    setJoiningGroup(true);
    try {
      const res = await fetch(`${API}/groups/join-by-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ invite_link: joinLinkInput.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.already_member) {
          toast.info(data.message);
        } else {
          toast.success(data.message);
        }
        setJoinLinkInput("");
        setGroupActionModal(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to join group");
      }
    } catch {
      toast.error("Failed to join group");
    } finally {
      setJoiningGroup(false);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      const res = await fetch(`${API}/groups/${groupId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setGroups((prev) => prev.filter((g) => g.group_id !== groupId));
        const newGm = { ...groupMembers };
        delete newGm[groupId];
        setGroupMembers(newGm);
        toast.success("Group deleted");
      }
    } catch {
      toast.error("Failed to delete group");
    }
  };

  const handleOpenGroupModal = (group) => {
    setSelectedGroup(group);
    setGroupModalOpen(true);
  };

  const handleGroupMembersChange = (groupId, members) => {
    setGroupMembers((prev) => ({ ...prev, [groupId]: members }));
  };

  const handleAddSection = async (name) => {
    if (!name?.trim()) return;
    const colors = ["#e74c3c", "#3498db", "#9b59b6", "#27ae60", "#f39c12", "#e67e22", "#1abc9c"];
    try {
      const res = await fetch(`${API}/sections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name.trim(), color: colors[customSections.length % colors.length] }),
      });
      if (res.ok) {
        const section = await res.json();
        setCustomSections(prev => [...prev, section]);
        toast.success("Section added");
      }
    } catch {
      toast.error("Failed to add section");
    }
  };

  const handleRenameSection = async (sectionId, newName) => {
    if (!newName?.trim()) return;
    try {
      const res = await fetch(`${API}/sections/${sectionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newName.trim() }),
      });
      if (res.ok) {
        const updated = await res.json();
        setCustomSections(prev => prev.map(s => s.section_id === sectionId ? updated : s));
        toast.success("Section renamed");
      }
    } catch {
      toast.error("Failed to rename");
    }
  };

  const handleDeleteSection = async (sectionId) => {
    try {
      const res = await fetch(`${API}/sections/${sectionId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setCustomSections(prev => prev.filter(s => s.section_id !== sectionId));
        fetchData();
        toast.success("Section deleted");
      }
    } catch {
      toast.error("Failed to delete section");
    }
  };

  const handleDeleteDefaultBoard = (boardId) => {
    // Remove tasks in this board
    const boardTasks = tasks.filter(t => t.status === boardId);
    if (boardTasks.length > 0) {
      const confirmed = window.confirm(`This section has ${boardTasks.length} task(s). Delete section and all its tasks?`);
      if (!confirmed) return;
      // Delete tasks from backend
      Promise.all(
        boardTasks.map(t =>
          fetch(`${API}/tasks/${t.task_id}`, { method: "DELETE", credentials: "include" })
        )
      ).then(() => {
        setTasks(prev => prev.filter(t => t.status !== boardId));
      });
    }
    setHiddenBoards(prev => [...prev, boardId]);
    toast.success("Section deleted");
  };

  const handleCopyAllTasks = async (sourceGroupId, targetGroupId, memberId) => {
    try {
      const res = await fetch(`${API}/groups/copy-tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ source_group_id: sourceGroupId, target_group_id: targetGroupId, member_id: memberId }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Copied ${data.copied} tasks to ${data.target}`);
        fetchTasks();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to copy tasks");
      }
    } catch {
      toast.error("Failed to copy tasks");
    }
  };


  const handleAddTaskToBoard = async (boardId) => {
    // Create task immediately and open detail panel (Any.do style)
    const priorityMap = {
      "urgent-important": "high",
      "followups": "medium",
      "important-regular": "medium",
      "event-followups": "low",
    };
    const boardNameMap = {
      "urgent-important": "Urgent and Important",
      "followups": "Followups",
      "important-regular": "Important & Regular",
      "event-followups": "Event Followups",
    };

    try {
      const res = await fetch(`${API}/tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          title: "",
          description: "",
          priority: priorityMap[boardId] || "medium",
          status: boardId,
          list_id: lists[0]?.list_id || "",
          tags: [],
          group_id: activeGroupId || null,
        }),
      });
      if (res.ok) {
        const newTask = await res.json();
        setTasks((prev) => [newTask, ...prev]);
        setDetailTask(newTask); // Open detail panel immediately
      } else {
        toast.error("Failed to create task");
      }
    } catch {
      toast.error("Failed to create task");
    }
  };

  // Drag and Drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const task = tasks.find(t => t.task_id === active.id);
    setActiveTask(task);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);
    
    if (!over) return;
    
    const taskId = active.id;
    const newBoardId = over.id;
    
    // Find the task
    const task = tasks.find(t => t.task_id === taskId);
    if (!task) return;
    
    // Map board to priority
    let newPriority = task.priority;
    switch (newBoardId) {
      case "urgent-important":
        newPriority = "high";
        break;
      case "followups":
      case "important-regular":
        newPriority = "medium";
        break;
      case "event-followups":
        newPriority = "low";
        break;
      default:
        break;
    }
    
    // Update if board changed
    if (newBoardId !== task.status || newPriority !== task.priority) {
      // Optimistic update
      setTasks((prev) =>
        prev.map((t) => (t.task_id === taskId ? { ...t, priority: newPriority, status: newBoardId } : t))
      );
      
      // Update on server
      try {
        const response = await fetch(`${API}/tasks/${taskId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ priority: newPriority, status: newBoardId }),
        });
        
        if (response.ok) {
          const updatedTask = await response.json();
          setTasks((prev) =>
            prev.map((t) => (t.task_id === taskId ? updatedTask : t))
          );
          toast.success("Task moved!");
        }
      } catch (error) {
        // Revert on error
        setTasks((prev) =>
          prev.map((t) => (t.task_id === taskId ? task : t))
        );
        toast.error("Failed to move task");
      }
    }
  };

  const handleDragOver = (event) => {
    // Optional: handle drag over for visual feedback
  };

  // Get task counts
  const getTaskCount = (filter) => {
    const today = new Date().toISOString().split('T')[0];
    const next7 = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    switch (filter) {
      case "my-day":
        return tasks.filter(t => !t.completed && t.due_date?.split('T')[0] === today).length;
      case "next-7":
        return tasks.filter(t => !t.completed && t.due_date && t.due_date.split('T')[0] >= today && t.due_date.split('T')[0] <= next7).length;
      case "all":
        return tasks.filter(t => !t.completed).length;
      default:
        return 0;
    }
  };

  // Group tasks by board/status - filtered by active group
  const getTasksByBoard = (boardId) => {
    return tasks.filter(t => {
      // Group filter: only show tasks for the active group
      if (activeGroupId) {
        if (t.group_id !== activeGroupId) return false;
      } else {
        // When no group is active, show only personal tasks (no group_id)
        if (t.group_id) return false;
      }
      // Member filter
      if (filterMemberId && (!t.assignees || !t.assignees.includes(filterMemberId))) {
        return false;
      }
      // Use status field if set (new tasks)
      if (t.status) return t.status === boardId;
      // Fallback: map by priority (legacy tasks)
      switch (boardId) {
        case "urgent-important":
          return t.priority === "high";
        case "followups":
          return t.priority === "medium" && t.description?.toLowerCase().includes("follow");
        case "important-regular":
          return t.priority === "medium" && !t.description?.toLowerCase().includes("follow");
        case "event-followups":
          return t.priority === "low";
        default:
          return false;
      }
    });
  };

  const handleClearCompleted = async () => {
    const completedTasks = tasks.filter(t => t.completed);
    if (completedTasks.length === 0) {
      toast.info("No completed tasks to clear");
      return;
    }
    
    try {
      await Promise.all(
        completedTasks.map(t =>
          fetch(`${API}/tasks/${t.task_id}`, {
            method: "DELETE",
            credentials: "include",
          })
        )
      );
      setTasks(prev => prev.filter(t => !t.completed));
      toast.success(`Cleared ${completedTasks.length} completed task(s)`);
    } catch {
      toast.error("Failed to clear completed tasks");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d21]">
        <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1d21] text-white flex" data-testid="dashboard">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 w-[240px] anydo-sidebar flex flex-col transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        {/* Workspace Header */}
        <div className="p-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img src="/mr_chows_logo.png" alt="Mr. Chow's" className="h-9 object-contain" />
          </div>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1">
          <div className="p-3 space-y-1">
            {/* Main Views */}
            <NavItem
              icon={<Sun className="w-5 h-5" />}
              label="My day"
              count={getTaskCount("my-day")}
              active={selectedView === "my-day" && !activeGroupId}
              onClick={() => { setSelectedView("my-day"); setActiveGroupId(null); closeSidebar(); }}
            />
            <NavItem
              icon={<CalendarDays className="w-5 h-5" />}
              label="Next 7 days"
              count={getTaskCount("next-7")}
              active={selectedView === "next-7" && !activeGroupId}
              onClick={() => { setSelectedView("next-7"); setActiveGroupId(null); closeSidebar(); }}
            />
            <NavItem
              icon={<Inbox className="w-5 h-5" />}
              label="All my tasks"
              count={getTaskCount("all")}
              active={selectedView === "all" && !activeGroupId}
              onClick={() => { setSelectedView("all"); setActiveGroupId(null); closeSidebar(); }}
            />
            <NavItem
              icon={<Calendar className="w-5 h-5" />}
              label="My Calendar"
              active={selectedView === "calendar" && !activeGroupId}
              onClick={() => { setSelectedView("calendar"); setActiveGroupId(null); closeSidebar(); }}            />
            
            {/* Custom Views */}
            <button
              onClick={() => setCustomViewsOpen(!customViewsOpen)}
              className="anydo-nav-item w-full"
            >
              <FileText className="w-5 h-5" />
              <span className="flex-1 text-left">Custom views</span>
              <ChevronRight className={`w-4 h-4 transition-transform ${customViewsOpen ? 'rotate-90' : ''}`} />
            </button>

            {/* My Lists Section */}
            <div className="pt-4">
              <div className="flex items-center justify-between px-2 py-2">
                <button
                  onClick={() => setMyListsOpen(!myListsOpen)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-400"
                >
                  <span>My lists</span>
                  <Lock className="w-3 h-3" />
                </button>
                <button
                  onClick={() => { setEditingList(null); setListModalOpen(true); }}
                  className="p-1 hover:bg-white/10 rounded transition-colors"
                  data-testid="add-list-btn"
                >
                  <Plus className="w-4 h-4 text-gray-400" />
                </button>
              </div>
              
              {myListsOpen && lists.map((list) => {
                const taskCount = tasks.filter(t => t.list_id === list.list_id && !t.completed).length;
                
                return (
                  <div key={list.list_id} className="group relative">
                    <NavItem
                      icon={<span className="text-lg">{getListEmoji(list.icon)}</span>}
                      label={list.name}
                      count={taskCount}
                      active={selectedView === `list-${list.list_id}`}
                      onClick={() => { setSelectedView(`list-${list.list_id}`); closeSidebar(); }}
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#2d3136] border-white/10">
                        <DropdownMenuItem onClick={() => { setEditingList(list); setListModalOpen(true); }}>
                          <Edit3 className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-400"
                          onClick={() => handleDeleteList(list.list_id)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>

            {/* Groups Section */}
            <div className="pt-4">
              <div className="flex items-center justify-between px-2 py-2">
                <button
                  onClick={() => setGroupsOpen(!groupsOpen)}
                  className="flex items-center gap-2 text-sm font-medium text-gray-400"
                >
                  <ChevronRight className={`w-3 h-3 transition-transform ${groupsOpen ? 'rotate-90' : ''}`} />
                  <span>My teams</span>
                </button>
                <div className="flex items-center gap-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        data-testid="add-group-btn"
                        className="p-1 hover:bg-white/10 rounded transition-colors"
                      >
                        <Plus className="w-4 h-4 text-gray-400" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-[#2d3136] border-white/10 w-48">
                      <DropdownMenuItem
                        data-testid="create-group-option"
                        onClick={() => setGroupActionModal("create")}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Group
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        data-testid="join-group-option"
                        onClick={() => setGroupActionModal("join")}
                      >
                        <Link2 className="w-4 h-4 mr-2" />
                        Join Group
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              
              {groupsOpen && groups.map((group) => {
                const members = groupMembers[group.group_id] || [];
                return (
                  <div key={group.group_id}>
                    {/* Group Header */}
                    <div className="group relative">
                      <button
                        data-testid={`group-${group.group_id}`}
                        onClick={() => {
                          setActiveGroupId(prev => prev === group.group_id ? null : group.group_id);
                          setSelectedView("all");
                          closeSidebar();
                        }}
                        className={`anydo-nav-item w-full ${activeGroupId === group.group_id ? 'bg-white/10' : ''}`}
                      >
                        <Users className="w-4 h-4 text-gray-400" />
                        <span className="flex-1 text-left text-sm font-medium">{group.name}</span>
                        <span className="text-xs text-gray-500">{members.length}</span>
                      </button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1 opacity-0 group-hover:opacity-100 hover:bg-white/10 rounded transition-opacity">
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-[#2d3136] border-white/10">
                          <DropdownMenuItem onClick={() => handleOpenGroupModal(group)}>
                            <Users className="w-4 h-4 mr-2" />
                            Manage Members
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-red-400"
                            onClick={() => handleDeleteGroup(group.group_id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete Group
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    {/* Group Members (sub-items) - admin/EA: view tasks panel, non-admin: filter or open admin updates */}
                    {members.map((member) => {
                      const EA_EMAIL_LC = "ea@mrchows.co.in";
                      const userEmailLC = (user?.email || "").toLowerCase();
                      const myMembership = members.find(m => (m.email || "").toLowerCase() === userEmailLC);
                      const isOwner = group.is_owner || group.owner_id === user?.user_id;
                      const isAdmin = isOwner || myMembership?.role === "admin" || userEmailLC === EA_EMAIL_LC;
                      const isSelf = (member.email || "").toLowerCase() === userEmailLC;
                      const notifCount = isAdmin
                        ? (memberNotifications[group.group_id]?.[member.member_id] || 0)
                        : (member.role === "admin" ? (adminNotifications[group.group_id] || 0) : 0);
                      return (
                        <div
                          key={member.member_id}
                          className={`anydo-nav-item w-full pl-8 flex items-center ${(activeGroupId === group.group_id && isSelf) || filterMemberId === member.member_id ? 'bg-white/10' : ''}`}
                        >
                          <button
                            className="flex items-center gap-2 flex-1 min-w-0"
                            data-testid={`filter-member-${member.member_id}`}
                            onClick={async () => {
                              // Always switch to this group's context first
                              setActiveGroupId(group.group_id);
                              setSelectedView("all");
                              setFilterMemberId(null); // Clear any member filter
                              closeSidebar();
                              
                              if (isAdmin && !isSelf) {
                                // Admin/EA clicking a member → open their task panel
                                setViewMember(member);
                                setViewMemberGroup(group);
                                // Mark member notifications as read
                                if (notifCount > 0) {
                                  try {
                                    await fetch(`${API}/groups/${group.group_id}/member-notifications/${member.member_id}/read`, {
                                      method: "POST", credentials: "include",
                                    });
                                    setMemberNotifications(prev => ({
                                      ...prev,
                                      [group.group_id]: { ...prev[group.group_id], [member.member_id]: 0 }
                                    }));
                                  } catch { /* silent */ }
                                }
                              } else if (!isAdmin && member.role === "admin") {
                                // Non-admin clicking an admin → open admin updates panel + mark as read
                                setViewAdmin(member);
                                setViewAdminGroup(group);
                                if (notifCount > 0) {
                                  try {
                                    await fetch(`${API}/groups/${group.group_id}/admin-notifications/read`, {
                                      method: "POST", credentials: "include",
                                    });
                                    setAdminNotifications(prev => ({ ...prev, [group.group_id]: 0 }));
                                  } catch { /* silent */ }
                                }
                              }
                              // For clicking self or non-admin clicking another non-admin: no action
                            }}
                          >
                            {/* Avatar with notification badge */}
                            <div className="relative flex-shrink-0">
                              <div
                                className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium text-white"
                                style={{ backgroundColor: member.color }}
                              >
                                {member.initials}
                              </div>
                              {notifCount > 0 && (
                                <div
                                  className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 flex items-center justify-center"
                                  data-testid={`notif-badge-${member.member_id}`}
                                >
                                  <span className="text-[9px] font-bold text-white leading-none">
                                    {notifCount > 99 ? "99+" : notifCount}
                                  </span>
                                </div>
                              )}
                            </div>
                            <span className="flex-1 text-left text-xs text-gray-400 truncate">{member.name}</span>
                            {member.role === "admin" && (
                              <span className="text-[10px] text-blue-400 font-medium flex-shrink-0">Admin</span>
                            )}
                          </button>
                          {/* Member actions: chat + copy tasks */}
                          {isAdmin && !isSelf && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button
                                  data-testid={`member-menu-${member.member_id}`}
                                  className="p-1 hover:bg-white/10 rounded transition-opacity"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <MoreHorizontal className="w-3.5 h-3.5 text-gray-500" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="w-52 bg-[#2d3136] border-white/10" align="end" onClick={(e) => e.stopPropagation()}>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setChatGroup(group);
                                    setChatIsAdmin(true);
                                  }}
                                >
                                  <MessageCircle className="w-4 h-4 mr-2 text-gray-400" />
                                  Chat
                                </DropdownMenuItem>
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger className="flex items-center">
                                    <Copy className="w-4 h-4 mr-2" />
                                    Copy tasks to
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent className="bg-[#2d3136] border-white/10">
                                    {groups.filter(g => g.group_id !== group.group_id).map(tg => (
                                      <DropdownMenuItem
                                        key={tg.group_id}
                                        onClick={() => handleCopyAllTasks(group.group_id, tg.group_id, member.member_id)}
                                      >
                                        <Users className="w-4 h-4 mr-2 text-gray-400" />
                                        {tg.name}
                                      </DropdownMenuItem>
                                    ))}
                                    {groups.filter(g => g.group_id !== group.group_id).length === 0 && (
                                      <DropdownMenuItem disabled>
                                        <span className="text-gray-500 text-xs">No other groups</span>
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {(!isAdmin || isSelf) && (
                            <button
                              data-testid={`chat-member-${member.member_id}`}
                              onClick={() => {
                                setChatGroup(group);
                                setChatIsAdmin(group.is_owner || group.owner_id === user?.user_id);
                              }}
                              className="p-1 hover:bg-white/10 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                              title="Chat"
                            >
                              <MessageCircle className="w-3.5 h-3.5 text-gray-500" />
                            </button>
                          )}
                          {filterMemberId === member.member_id && (
                            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 ml-1" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              
              {groups.length === 0 && groupsOpen && (
                <p className="px-4 py-2 text-xs text-gray-600">No groups yet</p>
              )}
            </div>
          </div>
        </ScrollArea>

        {/* User Section */}
        <div className="p-3 border-t border-white/10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/10 transition-colors">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="text-sm bg-blue-600">{user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <span className="text-sm truncate">{user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 bg-[#2d3136] border-white/10">
              <DropdownMenuItem onClick={handleLogout} className="text-red-400">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-[#1a1d21] border-b border-white/10 px-4 py-3">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-5 h-5" />
            </button>
            
            {/* Workspace Title */}
            <div className="flex items-center gap-3">
              <img src="/mr_chows_logo.png" alt="Mr. Chow's" className="h-8 object-contain" />
              <h1 className="text-lg font-semibold">
                {activeGroupId
                  ? groups.find(g => g.group_id === activeGroupId)?.name || "Group"
                  : `${user?.name?.split(' ')[0]}'s Workspace`}
              </h1>
              
              {/* Team Member Avatars - from all groups */}
              {(() => {
                const allMembers = Object.values(groupMembers).flat();
                const uniqueMembers = allMembers.filter((m, i, arr) => arr.findIndex(x => x.email === m.email) === i);
                return uniqueMembers.length > 0 ? (
                  <div className="flex items-center gap-1 ml-4">
                    <div className="flex -space-x-1.5">
                      {uniqueMembers.map((m) => (
                        <div
                          key={m.member_id}
                          title={m.name}
                          className="w-7 h-7 rounded-full border-2 border-[#1a1d21] flex items-center justify-center text-[10px] font-medium text-white cursor-pointer hover:z-10 hover:scale-110 transition-transform"
                          style={{ backgroundColor: m.color }}
                          onClick={() => setFilterMemberId(prev => prev === m.member_id ? null : m.member_id)}
                        >
                          {m.initials}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>

            <div className="flex-1" />

            {/* Header Actions */}
            <div className="flex items-center gap-2">
              <button className="p-2 hover:bg-white/10 rounded-lg" title="Board View">
                <LayoutGrid className="w-5 h-5 text-gray-400" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg" title="List View">
                <List className="w-5 h-5 text-gray-400" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg" title="Filter">
                <Filter className="w-5 h-5 text-gray-400" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg" title="Automation">
                <Zap className="w-5 h-5 text-gray-400" />
              </button>
              
              <button
                data-testid="clear-completed-btn"
                onClick={handleClearCompleted}
                className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:bg-white/10 rounded-lg transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" />
                Clear completed
              </button>
              
              {/* Member filter indicator */}
              {filterMemberId && (
                <button
                  data-testid="clear-member-filter"
                  onClick={() => setFilterMemberId(null)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm text-blue-400 bg-blue-500/10 hover:bg-blue-500/20 rounded-lg transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  Showing member's tasks
                </button>
              )}
            </div>

            {/* Right Icons */}
            <div className="flex items-center gap-1 ml-4 border-l border-white/10 pl-4">
              <button className="p-2 hover:bg-white/10 rounded-lg">
                <Clock className="w-5 h-5 text-gray-400" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg">
                <Bell className="w-5 h-5 text-gray-400" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg relative">
                <Users className="w-5 h-5 text-gray-400" />
              </button>
              <button className="p-2 hover:bg-white/10 rounded-lg">
                <Search className="w-5 h-5 text-gray-400" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        {selectedView === "my-day" && !activeGroupId ? (
          <MyDayView user={user} tasks={tasks} onTasksChange={fetchData} />
        ) : (
        <div className="flex-1 overflow-auto p-4 relative">
          {/* Watermark */}
          <div className="pointer-events-none fixed inset-0 z-0 flex items-center justify-center" style={{ top: '60px', left: '240px' }}>
            <img src="/mr_chows_logo.png" alt="" className="w-[400px] opacity-[0.04] select-none" draggable={false} />
          </div>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
          >
            <div className="board-container">
              {allBoards.map((board) => (
                <BoardColumn
                  key={board.id}
                  board={board}
                  tasks={getTasksByBoard(board.id)}
                  onAddTask={() => handleAddTaskToBoard(board.id)}
                  onToggleTask={handleToggleTask}
                  onEditTask={(task) => { setDetailTask(task); }}
                  onDeleteTask={handleDeleteTask}
                  lists={lists}
                  customTags={customTags}
                  onRename={!board.isDefault ? (name) => handleRenameSection(board.id, name) : null}
                  onDeleteSection={board.isDefault ? () => handleDeleteDefaultBoard(board.id) : () => handleDeleteSection(board.id)}
                />
              ))}
              
              {/* Add Section Button */}
              <div className="min-w-[300px] flex-shrink-0">
                <button
                  data-testid="add-section-btn"
                  onClick={() => {
                    const name = prompt("Enter section name:");
                    if (name) handleAddSection(name);
                  }}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 border-2 border-dashed border-white/10 rounded-xl text-gray-500 hover:text-gray-300 hover:border-white/20 transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span className="text-sm font-medium">Add section</span>
                </button>
              </div>
            </div>
            
            {/* Drag Overlay */}
            <DragOverlay>
              {activeTask ? (
                <TaskCard
                  task={activeTask}
                  lists={lists}
                  customTags={customTags}
                  isDragging
                />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
        )}
      </main>

      {/* Modals */}
      <TaskModal
        open={taskModalOpen}
        onOpenChange={setTaskModalOpen}
        task={editingTask}
        lists={lists}
        boards={allBoards}
        defaultBoard={selectedBoard}
        defaultListId={lists[0]?.list_id}
        onSave={(savedTask) => {
          if (editingTask) {
            setTasks((prev) => prev.map((t) => (t.task_id === savedTask.task_id ? savedTask : t)));
          } else {
            setTasks((prev) => [savedTask, ...prev]);
          }
          setSelectedBoard(null);
        }}
      />

      <ListModal
        open={listModalOpen}
        onOpenChange={setListModalOpen}
        list={editingList}
        onSave={(savedList) => {
          if (editingList) {
            setLists((prev) => prev.map((l) => (l.list_id === savedList.list_id ? savedList : l)));
          } else {
            setLists((prev) => [...prev, savedList]);
          }
        }}
      />

      {/* Task Detail Panel */}
      {detailTask && (
        <TaskDetailPanel
          task={detailTask}
          lists={lists}
          currentUser={user}
          activeGroupId={activeGroupId}
          onClose={() => setDetailTask(null)}
          onUpdate={(updatedTask) => {
            setTasks((prev) => prev.map((t) => (t.task_id === updatedTask.task_id ? updatedTask : t)));
            setDetailTask(updatedTask);
          }}
          onDelete={(taskId) => {
            setTasks((prev) => prev.filter((t) => t.task_id !== taskId));
            setDetailTask(null);
          }}
        />
      )}

      {/* Group Members Modal */}
      {groupModalOpen && selectedGroup && (
        <GroupMembersModal
          group={selectedGroup}
          onClose={() => { setGroupModalOpen(false); setSelectedGroup(null); }}
          onMembersChange={handleGroupMembersChange}
          currentUser={user}
        />
      )}

      {/* Create / Join Group Modal */}
      {groupActionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="group-action-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => { setGroupActionModal(false); setNewGroupName(""); setJoinLinkInput(""); }} />
          <div className="relative w-full max-w-sm bg-[#1e2126] rounded-xl border border-white/10 p-6 shadow-2xl">
            {groupActionModal === "create" ? (
              <>
                <h3 className="text-lg font-semibold text-white mb-4">Create New Group</h3>
                <Input
                  data-testid="create-group-name"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="Enter group name"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 mb-4"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreateGroup(); }}
                />
                <div className="flex gap-3 justify-end">
                  <Button
                    variant="ghost"
                    className="text-gray-400"
                    onClick={() => { setGroupActionModal(false); setNewGroupName(""); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="create-group-submit"
                    className="bg-blue-500 hover:bg-blue-600"
                    onClick={() => handleCreateGroup()}
                  >
                    Create
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-white mb-2">Join a Group</h3>
                <p className="text-sm text-gray-400 mb-4">Paste the invite link shared with you</p>
                <Input
                  data-testid="join-group-link"
                  value={joinLinkInput}
                  onChange={(e) => setJoinLinkInput(e.target.value)}
                  placeholder="Paste invite link here..."
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500 mb-4"
                  autoFocus
                  onKeyDown={(e) => { if (e.key === "Enter") handleJoinGroup(); }}
                />
                <div className="flex gap-3 justify-end">
                  <Button
                    variant="ghost"
                    className="text-gray-400"
                    onClick={() => { setGroupActionModal(false); setJoinLinkInput(""); }}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="join-group-submit"
                    className="bg-blue-500 hover:bg-blue-600"
                    onClick={handleJoinGroup}
                    disabled={joiningGroup}
                  >
                    {joiningGroup ? "Joining..." : "Join"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Chat Panel */}
      {chatGroup && (
        <ChatPanel
          group={chatGroup}
          currentUserEmail={user?.email}
          isAdmin={chatIsAdmin}
          onClose={() => setChatGroup(null)}
        />
      )}

      {/* Member Tasks Panel — read-only for non-admins */}
      {viewMember && viewMemberGroup && (() => {
        const EA_EMAIL = "ea@mrchows.co.in";
        const userEmailLower = (user?.email || "").toLowerCase();
        const isOwner = viewMemberGroup.is_owner || viewMemberGroup.owner_id === user?.user_id;
        const myMembership = (groupMembers[viewMemberGroup.group_id] || []).find(
          m => (m.email || "").toLowerCase() === userEmailLower
        );
        const isEA = userEmailLower === EA_EMAIL;
        const isAdmin = isOwner || myMembership?.role === "admin" || isEA;
        return (
          <MemberTasksPanel
            group={viewMemberGroup}
            member={viewMember}
            currentUserEmail={user?.email}
            isAdmin={isAdmin}
            onClose={() => { setViewMember(null); setViewMemberGroup(null); }}
          />
        );
      })()}

      {/* Member viewing Admin Updates Panel */}
      {viewAdmin && viewAdminGroup && (
        <AdminUpdatesPanel
          group={viewAdminGroup}
          adminMember={viewAdmin}
          currentUserEmail={user?.email}
          onClose={() => { setViewAdmin(null); setViewAdminGroup(null); }}
        />
      )}
    </div>
  );
}

// Navigation Item Component
function NavItem({ icon, label, count, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`anydo-nav-item w-full ${active ? "active" : ""}`}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {icon}
      <span className="flex-1 text-left text-sm">{label}</span>
      {count > 0 && (
        <span className="text-xs px-1.5 py-0.5 rounded bg-white/10">{count}{count > 99 ? '+' : ''}</span>
      )}
    </button>
  );
}

// Helper to get emoji for list icon
function getListEmoji(icon) {
  const emojiMap = {
    inbox: "📥",
    briefcase: "💼",
    user: "👤",
    calendar: "📅",
    star: "⭐",
    list: "📋",
    home: "🏠",
    shopping: "🛒",
    book: "📚",
    heart: "❤️",
  };
  return emojiMap[icon] || "📋";
}
